import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";

/**
 * Control bar visibility: shown on pointer activity, hidden after `delay`.
 * `canHover` is true for mouse users — they toggle play by clicking the
 * video; on touch a tap only reveals the bar.
 */
export function useAutoHideControls(delay = 3000) {
  const [controlsVisible, setControlsVisible] = useState(true);
  const [canHover, setCanHover] = useState(
    () => window.matchMedia("(hover: hover)").matches,
  );
  const hideRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showControls = useCallback(() => {
    setControlsVisible(true);
    if (hideRef.current) clearTimeout(hideRef.current);
    hideRef.current = setTimeout(() => setControlsVisible(false), delay);
  }, [delay]);

  useEffect(() => {
    showControls();
    return () => {
      if (hideRef.current) clearTimeout(hideRef.current);
    };
  }, [showControls]);

  useEffect(() => {
    const mq = window.matchMedia("(hover: hover)");
    const onChange = () => setCanHover(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return { controlsVisible, showControls, canHover };
}

/** Mirrors a <video>'s play / mute / time state for VideoControlBar. */
export function useVideoState(
  videoRef: RefObject<HTMLVideoElement | null>,
  { trackTime = false }: { trackTime?: boolean } = {},
) {
  const [paused, setPaused] = useState(true);
  const [muted, setMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const syncVolume = () => setMuted(video.muted || video.volume === 0);
    const syncPaused = () => setPaused(video.paused);
    const syncTime = () => setCurrentTime(video.currentTime);
    const syncDuration = () => setDuration(video.duration || 0);
    syncVolume();
    syncPaused();
    video.addEventListener("volumechange", syncVolume);
    video.addEventListener("play", syncPaused);
    video.addEventListener("pause", syncPaused);
    if (trackTime) {
      syncTime();
      syncDuration();
      video.addEventListener("timeupdate", syncTime);
      video.addEventListener("seeked", syncTime);
      video.addEventListener("durationchange", syncDuration);
      video.addEventListener("loadedmetadata", syncDuration);
    }
    return () => {
      video.removeEventListener("volumechange", syncVolume);
      video.removeEventListener("play", syncPaused);
      video.removeEventListener("pause", syncPaused);
      video.removeEventListener("timeupdate", syncTime);
      video.removeEventListener("seeked", syncTime);
      video.removeEventListener("durationchange", syncDuration);
      video.removeEventListener("loadedmetadata", syncDuration);
    };
  }, [videoRef, trackTime]);

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    if (!video.muted && video.volume === 0) video.volume = 1;
  };

  const seek = (time: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = time;
    setCurrentTime(time);
  };

  return { paused, muted, currentTime, duration, toggleMute, seek };
}
